import { Link } from "react-router-dom";
import { ArrowRight, FileSearch, ShieldCheck, Award, Calendar } from "lucide-react";
import { PaidLeadForm } from "@/components/landing/PaidLeadForm";
import {
  SidebarCallCTA,
  SidebarEditorialStandards,
} from "@/components/site/BlogSidebar";
import { LeadMagnetCTA } from "@/components/site/LeadMagnetCTA";
import { brand, founder } from "@/data/site/brand";
import franklinPhoto from "@/assets/franklin-sofi.webp";
import type { BlogPost } from "@/data/site/blog";

/**
 * Topic → recommended service. The first matched keyword wins.
 * Keywords are matched against the post's slug, title, and category
 * (lowercased) so editors don't have to tag posts manually.
 */
const TOPIC_SERVICES: { keywords: string[]; href: string; label: string; pain: string }[] = [
  {
    keywords: ["garnishment", "wage levy", "wage-levy", "paycheck"],
    href: "/services/irs-collections-defense/wage-garnishment",
    label: "Wage Garnishment Release",
    pain: "Stop IRS wage garnishments — often within days.",
  },
  {
    keywords: ["bank levy", "levy"],
    href: "/services/irs-collections-defense/bank-levy",
    label: "Bank Levy Release",
    pain: "Get a frozen account released and stop further seizures.",
  },
  {
    keywords: ["offer in compromise", "oic", "settle", "pennies"],
    href: "/services/tax-debt-relief/offer-in-compromise",
    label: "Offer in Compromise",
    pain: "Settle IRS debt for less than you owe — when you qualify.",
  },
  {
    keywords: ["installment", "payment plan", "agreement"],
    href: "/services/tax-debt-relief/installment-agreements",
    label: "Installment Agreements",
    pain: "Negotiate an affordable monthly IRS payment plan.",
  },
  {
    keywords: ["penalty", "abatement"],
    href: "/services/tax-debt-relief/penalty-abatement",
    label: "Penalty Abatement",
    pain: "Remove failure-to-file and failure-to-pay penalties.",
  },
  {
    keywords: ["audit", "examination"],
    href: "/services/audit-defense/irs-audit-representation",
    label: "IRS Audit Representation",
    pain: "We respond, represent, and negotiate so you never face an auditor alone.",
  },
  {
    keywords: ["unfiled", "back taxes", "back-taxes", "missing return"],
    href: "/services/tax-debt-relief/back-taxes",
    label: "Back Taxes & Unfiled Returns",
    pain: "Get current with the IRS and stop the penalty meter.",
  },
  {
    keywords: ["payroll", "941", "trust fund"],
    href: "/services/business-tax/payroll-tax",
    label: "Payroll Tax Problems",
    pain: "Resolve 941/940 liabilities before they shut your business down.",
  },
  {
    keywords: ["cnc", "currently not collectible", "hardship"],
    href: "/services/irs-collections-defense/cnc-status",
    label: "Currently Not Collectible Status",
    pain: "Pause IRS collections when paying would create hardship.",
  },
  {
    keywords: ["lien", "federal tax lien"],
    href: "/services/irs-collections-defense/tax-liens",
    label: "Federal Tax Lien Release",
    pain: "Get a federal tax lien released, withdrawn, or subordinated.",
  },
  {
    keywords: ["innocent spouse"],
    href: "/services/tax-debt-relief/innocent-spouse",
    label: "Innocent Spouse Relief",
    pain: "Separate yourself from a spouse's IRS liability.",
  },
];

function pickService(post: BlogPost) {
  const haystack = `${post.slug} ${post.title} ${post.category}`.toLowerCase();
  for (const t of TOPIC_SERVICES) {
    if (t.keywords.some((k) => haystack.includes(k))) return t;
  }
  // Default fallback — general tax debt relief.
  return {
    href: "/services/tax-debt-relief",
    label: "Tax Debt Relief",
    pain: "End the IRS pressure with a CPA-led resolution plan.",
  };
}

/** Sticky right-rail conversion stack for blog posts. */
export const BlogPostSidebar = ({
  post,
  related,
}: {
  post: BlogPost;
  related: BlogPost[];
}) => {
  const service = pickService(post);
  const keyword = post.category.toLowerCase();

  return (
    <aside className="lg:sticky lg:top-24 space-y-5">
      {/* 1. Lead capture — peak engagement during read */}
      <PaidLeadForm
        source={`blog:${post.slug}`}
        keyword={keyword}
        location={undefined}
      />

      {/* 2. Phone CTA */}
      <SidebarCallCTA />

      {/* 2b. Lead magnet */}
      <LeadMagnetCTA variant="card" />


      {/* 3. Topic-matched service card */}
      <div className="bg-card border-l-4 border-gold border-y border-r border-border p-5 rounded-md">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-gold-dark mb-1">
          Get help with this
        </p>
        <h4 className="font-heading font-bold text-navy text-base mb-2 leading-tight">
          {service.label}
        </h4>
        <p className="text-xs text-muted-foreground mb-3 leading-relaxed">{service.pain}</p>
        <Link
          to={service.href}
          className="text-xs font-semibold text-navy hover:text-gold inline-flex items-center gap-1"
        >
          Learn how it works <ArrowRight className="w-3 h-3" />
        </Link>
      </div>

      {/* 4. Free tool teaser — Notice Lookup */}
      <div className="bg-navy-dark text-white p-5 rounded-md">
        <FileSearch className="w-5 h-5 text-gold mb-2" />
        <h4 className="font-heading font-bold text-base mb-1">Got an IRS letter?</h4>
        <p className="text-xs text-white/75 mb-3 leading-relaxed">
          Decode any CP or LT notice in 30 seconds with our free Notice Lookup tool.
        </p>
        <Link
          to="/tools/irs-notice-lookup"
          className="inline-flex items-center gap-1 text-xs font-bold text-gold hover:text-gold-light"
        >
          Look up my notice <ArrowRight className="w-3 h-3" />
        </Link>
      </div>

      {/* 5. Author / credibility mini-card */}
      <div className="bg-card border border-border p-5 rounded-md">
        <div className="flex items-start gap-3 mb-3">
          <img
            src={franklinPhoto}
            alt={`${founder.name}, ${founder.credentials}`}
            className="w-14 h-14 rounded-full object-cover flex-shrink-0"
            loading="lazy"
           width={80} height={80} />
          <div>
            <p className="font-heading font-bold text-navy text-sm leading-tight">
              {founder.name}
            </p>
            <p className="text-[11px] text-muted-foreground">{founder.credentials}</p>
            <p className="text-[11px] text-muted-foreground">{founder.title}</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground mb-3">
          <Award className="w-3 h-3 text-gold-dark" />
          {brand.yearsExperience}+ years of IRS representation
        </div>
        <Link
          to="/about/franklin-sofi"
          className="text-xs font-semibold text-navy hover:text-gold inline-flex items-center gap-1"
        >
          About the author <ArrowRight className="w-3 h-3" />
        </Link>
      </div>

      {/* 6. Related posts */}
      {related.length > 0 && (
        <div className="bg-card border border-border p-5 rounded-md">
          <h4 className="font-heading font-bold text-navy text-sm uppercase tracking-wider mb-3">
            Related reading
          </h4>
          <ul className="space-y-3">
            {related.map((r) => (
              <li key={r.slug}>
                <Link
                  to={`/blog/${r.slug}`}
                  className="group block"
                >
                  <p className="text-sm font-semibold text-navy group-hover:text-gold leading-snug mb-1">
                    {r.title}
                  </p>
                  <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    <Calendar className="w-3 h-3" />
                    {new Date(r.date).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* 7. Editorial standards */}
      <SidebarEditorialStandards />

      {/* Trust microcopy */}
      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground px-1">
        <ShieldCheck className="w-3 h-3 text-emerald-600" />
        Reviewed by a credentialed CPA
      </div>
    </aside>
  );
};
